import { cityByAirport } from '@voll-intelligence/domain';

import { askDate, type NextServiceAnchor, type NextServiceAskRules } from '../core/ask.js';
import type { FlightNextServicesBlock } from '@voll-intelligence/genui-contract';

/** O pós-aéreo ancora na CIDADE de chegada (IATA → cidade do domain). */
export const FLIGHT_ASK_RULES: NextServiceAskRules = {
  hotel: {
    window: {
      range: 'flight.nextServices.ask.hotelRange',
      open: 'flight.nextServices.ask.hotelOpen',
      bare: 'flight.nextServices.ask.hotelBare',
    },
  },
  car: {
    window: {
      range: 'flight.nextServices.ask.carRange',
      open: 'flight.nextServices.ask.carOpen',
      bare: 'flight.nextServices.ask.carBare',
    },
  },
  train: { anchored: 'flight.nextServices.ask.train' },
  bus: { anchored: 'flight.nextServices.ask.bus' },
  seat: { fixed: 'flight.nextServices.ask.seat' },
  flight: { fixed: 'flight.nextServices.ask.flight' },
  insurance: { fixed: 'flight.nextServices.ask.insurance' },
  // Traslados: o pedido é fixo por perna — as pontas, a hora e o voo o agente
  // tira da viagem, não do texto.
  transfer_to_airport: { fixed: 'flight.nextServices.ask.transferToAirport' },
  transfer_airport_hotel: { fixed: 'flight.nextServices.ask.transferAirportHotel' },
  transfer_to_hotel: { fixed: 'flight.nextServices.ask.transferToHotel' },
};

/** Mesma cidade por `cityByAirport` (SAO/GRU/CGH contam, ex: chega num
 *  aeroporto, sai de outro do mesmo metro); quando um dos dois lados não
 *  resolve (código desconhecido), cai para comparação pelo IATA cru. */
function sameCity(a: string, b: string): boolean {
  const cityA = cityByAirport(a);
  const cityB = cityByAirport(b);
  if (cityA !== null && cityB !== null) return cityA === cityB;
  return a.toUpperCase() === b.toUpperCase();
}

/** Carrinho multi-trecho: N itens `flight` one-way (um por trecho, sem
 *  `return` — ver `buildFlightNextServicesBlockMulti` no agents). O check-out
 *  do hotel/carro não é o fim da viagem inteira: é a partida do PRÓXIMO trecho
 *  (na ordem de `items`, depois do primeiro voo) que sai da MESMA cidade em
 *  que o primeiro trecho chegou — o trecho que efetivamente deixa a cidade
 *  hospedada. Sem sucessor (último trecho, ida simples) → `undefined`, e a
 *  janela degrada para "a partir de" (QA 0914, E2: herdava o fim da viagem
 *  inteira em vez da partida do trecho seguinte). */
function nextLegDepartAt(
  items: FlightNextServicesBlock['items'],
  firstFlightIndex: number,
  destIata: string,
): string | undefined {
  for (const item of items.slice(firstFlightIndex + 1)) {
    if (item.productType !== 'flight') continue;
    if (sameCity(item.detail.outbound.from, destIata)) return item.detail.outbound.departAt;
  }
  return undefined;
}

type FlightCheckoutItem = Extract<
  FlightNextServicesBlock['items'][number],
  { productType: 'flight' }
>;

/** Fonte do check-out/devolução: ida+volta usa `detail.return`; multi-trecho
 *  (sem `return`) usa `nextLegDepartAt`. */
function resolveCheckOutDepartAt(
  flight: FlightCheckoutItem | undefined,
  items: FlightNextServicesBlock['items'],
  destIata: string,
): string | undefined {
  if (!flight) return undefined;
  if (flight.detail.return) return flight.detail.return.departAt;
  return nextLegDepartAt(items, items.indexOf(flight), destIata);
}

/** A cidade de chegada e a janela da viagem pelos segmentos do carrinho. */
export function flightAnchor(block: FlightNextServicesBlock, locale: string): NextServiceAnchor {
  const flight = block.items.find((item) => item.productType === 'flight');
  const outbound = flight?.detail.outbound;
  const iata = outbound?.to ?? '';
  return {
    param: 'city',
    value: cityByAirport(iata) ?? iata,
    // Check-in na CHEGADA ao destino (red-eye chega no dia seguinte ao embarque).
    from: askDate(outbound?.arriveAt, locale) ?? askDate(outbound?.departAt, locale),
    to: askDate(resolveCheckOutDepartAt(flight, block.items, iata), locale),
  };
}
